import { Map } from 'immutable';
import { v4 } from 'uuid';

import { getUploadedPhotoError } from '../validations/photos';
import { getUploadedVideoError } from '../validations/videos';
import { drawImageOnCanvas } from '../photoEditor/canvas';
import { fileToArrayBuffer } from '../files';
import { shapeId } from '../listings/listings';

import { EXT_TO_MIME as PHOTO_EXT_TO_MIME, MIME as PHOTO_MIME, MIME_TO_EXT as PHOTO_MIME_TO_EXT } from '../../constants/photos';
import { EXT_TO_MIME as VIDEO_EXT_TO_MIME, MIME_TO_EXT as VIDEO_MIME_TO_EXT } from '../../constants/videos';
import { MEDIA_TYPE } from '../../constants/studio';
import { ERRORS } from '../../constants/validations';
import { SECOND } from '../../constants/time';
import { EMPTY } from '../../constants';

export function shapeFoldersForApp(result, folder) {
  const { folder_name: name, image_count, video_count, uuid } = folder;

  const count = image_count || video_count || 0;

  function updateById(byId = EMPTY.MAP) {
    return byId.set(uuid, Map({ count: parseInt(count), name, uuid }));
  }

  function updateIds(ids = EMPTY.LIST) {
    return ids.push(uuid);
  }

  return result
    .update('byId', updateById)
    .update('ids', updateIds);
}

export function shapeImagesForApp(result, image) {
  const {
    alt_text: altText,
    connected_listing_count: listings,
    file_name: fileName,
    format,
    group_id: groupId,
    url,
    uuid,
  } = image;

  // TODO: check for API errors
  if (result.hasIn(['byId', uuid])) return result;

  function updateById(byId = EMPTY.MAP) {
    return byId.set(uuid,
      Map({
        altText,
        fileName,
        format,
        groupId,
        listings: parseInt(listings) || 0,
        mime: PHOTO_EXT_TO_MIME[format],
        url,
        uuid,
      })
    );
  }

  function updateIds(ids = EMPTY.LIST) {
    return ids.push(uuid);
  }

  return result
    .update('byId', updateById)
    .update('ids', updateIds);
}

export function shapeVideosForApp(result, video) {
  const {
    connected_listing_count: listings,
    file_name: fileName,
    format,
    group_id: groupId,
    url,
    uuid,
    thumbnail_url: thumbnailUrl,
  } = video;

  if (result.hasIn(['byId', uuid])) return result;

  function updateById(byId = EMPTY.MAP) {
    return byId.set(uuid,
      Map({
        fileName,
        format,
        groupId,
        listings: parseInt(listings) || 0,
        mime: VIDEO_EXT_TO_MIME[format],
        url,
        uuid,
        thumbnailUrl,
      })
    );
  }

  function updateIds(ids = EMPTY.LIST) {
    return ids.push(uuid);
  }

  return result
    .update('byId', updateById)
    .update('ids', updateIds);
}

export function shapeFileForApp({ channel, file, type }) {
  switch (type) {
    case MEDIA_TYPE.PHOTOS: {
      return new Promise(async function executor(resolve, reject) {
        try {
          await getUploadedPhotoError({ channel, file });
          const { name: fileName, type: mime } = file;
          const format = PHOTO_MIME_TO_EXT[mime];

          if (!format) {
            reject(ERRORS.PHOTOS.UNSUPPORTED_FORMAT[channel]);
            return;
          }

          const data = await fileToArrayBuffer(file);

          resolve({
            data,
            fileName,
            format,
            mime,
            url: URL.createObjectURL(file),
            uuid: v4(),
          });
        } catch (error) {
          reject(error);
        }
      });
    }

    case MEDIA_TYPE.VIDEOS: {
      return new Promise(async function executor(resolve, reject) {
        const url = URL.createObjectURL(file);

        function onValidated() {
          const { name: fileName, type: mime } = file;
          const format = VIDEO_MIME_TO_EXT[mime];

          if (!format) {
            reject(ERRORS.VIDEOS.UNSUPPORTED_FORMAT[channel]);
            return;
          }

          function onVideoData(videoData) {
            const element = document.createElement('video');
            element.addEventListener('error', reject);

            function onPlay() {
              const { duration, videoHeight: height, videoWidth: width } = element;
              element.pause();

              if (duration > 5) {
                element.currentTime = SECOND * 5;
              } else if (duration > 1) {
                element.currentTime = SECOND;
              } else {
                element.currentTime = 0;
              }

              function onThumbnailBlob(thumbnailBlob) {
                const thumbnailReader = new FileReader();

                function onThumbnailReaderLoad() {
                  resolve({
                    data: videoData,
                    fileName,
                    format,
                    mime,
                    thumbnailData: thumbnailReader.result,
                    thumbnailFormat: PHOTO_MIME_TO_EXT[PHOTO_MIME.PNG],
                    thumbnailMime: PHOTO_MIME.PNG,
                    thumbnailUrl: URL.createObjectURL(thumbnailBlob),
                    url: URL.createObjectURL(file),
                    uuid: v4(),
                  });
                }

                thumbnailReader.addEventListener('error', reject);
                thumbnailReader.addEventListener('load', onThumbnailReaderLoad);
                thumbnailReader.readAsArrayBuffer(thumbnailBlob);
              }

              drawImageOnCanvas(element, 0, 0, width, height)
                .getBlob()
                .then(onThumbnailBlob)
                .catch(reject);
            }

            element.muted = true;
            element.playsInline = true;
            element.src = url;
            element.play().then(onPlay).catch(reject);
          }

          fileToArrayBuffer(file).then(onVideoData).catch(reject);
        }

        getUploadedVideoError({ channel, file, url }).then(onValidated).catch(reject);
      });
    }

    default: {
      return Promise.reject(`Unknown type: ${type}`);
    }
  }
}

export function shapeListings(listings) {
  function reduceListings(result, listing) {
    const id = shapeId(listing.listing_id);

    result.byId[id] = listing;
    result.ids.push(id);
    return result;
  }

  return listings.reduce(reduceListings, { byId: {}, ids: [] });
}

